// Utility functions for handling recurrence rules and formatting

import { DateTime } from 'luxon';
import { RRule } from 'rrule';
import { displayUserTimeZone, getDateOrdinal } from './dateUtils';
import { capitalizeWord } from './stringUtils';

///////////////////////////Rrule helpers

/**
 * Convert a date from one timezone to another while keeping the same local time
 * This assumes the input date is from an RRule or similar, where the time is meant to be interpreted in a specific timezone (e.g., the schedule's timezone)
 * @param {Date} dateTime - The JavaScript Date to convert
 * @param {string} fromTzid - The IANA timezone identifier to convert from (e.g., 'America/New_York', 'UTC')
 * @param {string} toTzid - The IANA timezone identifier to convert to (e.g., 'America/Los_Angeles', 'UTC')
 * @returns {Date} The converted date as a JavaScript Date object with the same local time in the target timezone
 */
function convertRRuleTimeFromTo(dateTime, fromTzid, toTzid) {
  // Given a 'bad' JS Date Object, assume it's in UTC.
  // Override the timezone to be the 'fromTzid'.
  // Then convert to the 'toTzid'.
  // ex: 00UTC -> 00EST, 00EST -> 00PST (03EST)
  let luxonDate = DateTime.fromJSDate(dateTime, { zone: fromTzid });
  let converted = luxonDate.setZone(toTzid, { keepLocalTime: true });

  return converted.toJSDate();
}

/**
 * Convert a dtstart date to the intended time zone based on the schedule
 * @param {Object} options - Options object from RRule parsing
 * @param {Date} options.dtstart - The JavaScript Date to convert (assumed to be in local time)
 * @param {string} options.tzid - The IANA timezone identifier to convert to (e.g., 'America/New_York', 'UTC')
 * @returns {Date} The converted date as a JavaScript Date object with the same local time in the target timezone
 */
export function getFixedDtstart(options) {
  return convertRRuleTimeFromTo(options.dtstart, 'local', options.tzid);
}

function getLocalOfRRuleTime(date) {
  return convertRRuleTimeFromTo(date, 'utc', 'local');
}

/**
 * Format a 24-hour time to 12-hour format with AM/PM
 * @param {number} hour24 - Hour in 24-hour format
 * @param {number} [minute=0] - Minute value
 * @returns {string} Formatted time string (e.g., "3:00 PM")
 */
function formatHour(hour24, minute = 0) {
  const hour = ((hour24 % 24) + 24) % 24;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  const min = minute.toString().padStart(2, '0');
  return `${hour12}:${min} ${ampm}`;
}

/**
 * Format an RRULE string to human-readable text with timezone-adjusted times
 * Converts the RRULE's natural language description and adjusts any specified hours
 * from the rule's timezone to the user's local timezone
 * @param {string} rruleString - The RRULE string to format (e.g., "FREQ=DAILY;BYHOUR=14")
 * @returns {string} Formatted human-readable text with adjusted times and timezone (e.g., "Every day at 9:00 AM EST")
 */
export function formatRruleText(rruleString) {
  const rrule = RRule.fromString(rruleString);
  const text = rrule.toText();

  let localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const originalTzOffset = getOffsetHours(rrule.options.tzid || localTimeZone);
  const userTzOffset = getOffsetHours(localTimeZone);
  const offsetDiff = userTzOffset - originalTzOffset;

  // Format hours from byhour options
  const formattedTimes = Array.isArray(rrule.options.byhour)
    ? rrule.options.byhour
        .map((hour, idx) => {
          const adjustedHour = hour + offsetDiff;
          const minute = rrule.options.byminute?.[idx] ?? rrule.options.byminute?.[0] ?? 0;
          return { hour: adjustedHour, minute, formatted: formatHour(adjustedHour, minute) };
        })
        .sort((a, b) => {
          const aTime = ((a.hour % 24) + 24) % 24;
          const bTime = ((b.hour % 24) + 24) % 24;
          return aTime !== bTime ? aTime - bTime : a.minute - b.minute;
        })
        .map((t) => t.formatted)
    : [];

  // Build the final text
  const baseText = capitalizeWord(text.trim());
  const timeZone = displayUserTimeZone();

  if (formattedTimes.length > 0) {
    // Remove existing " at ..." portion if present
    const textWithoutAt = baseText.includes(' at ') ? baseText.split(' at ')[0] : baseText;
    return `${textWithoutAt} at ${formattedTimes.join(', ')} ${timeZone}`;
  } else {
    return `${baseText} ${timeZone}`;
  }
}

/**
 * Get the offset in hours for a given timezone and date
 * @param {string} tzid - The IANA timezone identifier (e.g., 'America/New_York', 'UTC')
 * @param {Date} [date=new Date()] - The date to calculate the offset for (defaults to current date)
 * @returns {number} The timezone offset in hours (can be fractional)
 */
function getOffsetHours(tzid, date = new Date()) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tzid,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = dtf.formatToParts(date);
  const [year, month, day, hour, minute, second] = [
    parts.find((p) => p.type === 'year').value,
    parts.find((p) => p.type === 'month').value,
    parts.find((p) => p.type === 'day').value,
    parts.find((p) => p.type === 'hour').value,
    parts.find((p) => p.type === 'minute').value,
    parts.find((p) => p.type === 'second').value,
  ];
  // Create a date string in the target timezone
  const asUTC = Date.UTC(year, month - 1, day, hour, minute, second);
  // Offset in minutes
  const offsetMinutes = (asUTC - date.getTime()) / 60000;
  // Offset in hours
  let intOffset = parseInt(offsetMinutes, 10) / 60;
  return intOffset;
}

///////////////////////////Rrule fixes and generation

// Map of weekday numbers to RRULE weekday codes
const weekdayMap = {
  0: 'MO',
  1: 'TU',
  2: 'WE',
  3: 'TH',
  4: 'FR',
  5: 'SA',
  6: 'SU',
};

export function getWeekdayMap() {
  return weekdayMap;
}

// Map of occurrence numbers to ordinal text
const ordinalMap = {
  1: 'first',
  2: 'second',
  3: 'third',
  4: 'fourth',
  5: 'fifth',
  '-1': 'last',
  '-2': 'nexttolast',
};

export function getOrdinalMap() {
  return ordinalMap;
}

/**
 * Convert BYNWEEKDAY clause to proper BYDAY format with ordinals
 * @param {string} rruleString - Full RRULE string potentially containing BYNWEEKDAY
 * @returns {string|null} Formatted BYDAY value (e.g., "+1MO", "-1FR") or null if not found
 */
function bynweekdayToOrdinal(rruleString) {
  const bynweekdayMatch = rruleString.match(/BYNWEEKDAY=([^;]+)/);
  if (!bynweekdayMatch) return null;

  const encodedValue = bynweekdayMatch[1];

  // Expected format: "0,1" where 0 is the weekday (0=Monday) and 1 is the occurrence (1=first, -1=last, etc.)
  const parts = encodedValue.split(',');
  // Add sign for positive occurrences (e.g., +1MO for first Monday)
  const sign = parts[1].length === 1 ? '+' : '';
  const byday = sign + parts[1] + weekdayMap[parts[0]];
  return byday;
}

/**
 * Fix BYNWEEKDAY clause by converting it to proper BYDAY format
 * @param {string} rruleString - Full RRULE string potentially containing BYNWEEKDAY
 * @returns {string} RRULE string with BYNWEEKDAY converted to BYDAY, or original string if no BYNWEEKDAY found
 */
function fixBYNWEEKDAY(rruleString) {
  if (!rruleString.includes('BYNWEEKDAY')) {
    return rruleString;
  }
  let newRruleString = rruleString;
  const ordinalText = bynweekdayToOrdinal(newRruleString);
  if (ordinalText) {
    newRruleString = newRruleString.replace(/BYNWEEKDAY=[^;]+/, `BYWEEKDAY=${ordinalText}`);
  }
  return newRruleString;
}

/**
 * Normalize BYWEEKDAY (non-standard alias) and unsigned ordinal weekday tokens to the
 * form that rrule.js expects: BYDAY with an explicit sign on positive ordinals.
 *
 * Examples:
 *   BYWEEKDAY=1MO  → BYDAY=+1MO   (first Monday)
 *   BYWEEKDAY=+1MO → BYDAY=+1MO   (already signed, just rename)
 *   BYDAY=2TU      → BYDAY=+2TU   (second Tuesday, add sign)
 *   BYDAY=-1FR     → BYDAY=-1FR   (last Friday, unchanged)
 *   BYDAY=MO,TU    → BYDAY=MO,TU  (simple weekdays, unchanged)
 *
 * @param {string} rruleString - RRULE string to normalize
 * @returns {string} Normalized RRULE string
 */
function fixBYWEEKDAY(rruleString) {
  return rruleString.replace(/BY(?:WEEKDAY|DAY)=([^;\n]+)/g, (_match, value) => {
    const normalized = value
      .split(',')
      .map((token) => {
        const t = token.trim();
        // Token starts with a digit → positive ordinal without explicit sign, add '+'
        if (/^\d/.test(t)) {
          return '+' + t;
        }
        return t;
      })
      .join(',');
    return `BYDAY=${normalized}`;
  });
}

/**
 * Generate an RRule string for an assignment, parameterised by context mode.
 *
 * All four modes preserve the schedule's BYHOUR / BYMINUTE so the assignment
 * recurrence always refers to the same time-of-day as the original rule.
 *
 * Mode behaviour:
 *  'calendar'  — single exact occurrence (FREQ=DAILY;COUNT=1) whose DTSTART is
 *                the event's specific dated start.  Intended for calendar-view
 *                cards where the user is looking at one concrete instance.
 *
 *  'weekly'    — matches every recurrence on the same weekday and time
 *                (FREQ=WEEKLY;BYDAY=<DOW>).  DTSTART comes from the schedule's
 *                own dtstart so the rule is not anchored to a particular date.
 *
 *  'monthly'   — matches every recurrence on the same day-of-month
 *                (FREQ=MONTHLY;BYMONTHDAY=<D>).  Pattern-view semantics.
 *
 *  'yearly'    — matches every recurrence on the same month+day
 *                (FREQ=YEARLY;BYMONTH=<M>;BYMONTHDAY=<D>).  Pattern-view.
 *
 * The event's `start` is assumed to be in local display time, consistent with
 * how `generateEventsBetween` returns events (UTC wall-clock → local via
 * keepLocalTime).  For 'calendar' mode we reverse that convention when building
 * the DTSTART.
 *
 * @param {Date}   eventStart - Event start in local display time.
 * @param {Object} schedule   - Schedule object with a `recurrence` string.
 * @param {'calendar'|'weekly'|'monthly'|'yearly'} mode
 * @returns {string} RRule string for the assignment recurrence.
 */

/**
 * Build an RRule that matches **all future occurrences** of a schedule but
 * scoped to the time slot of the selected event (i.e. same hour:minute as the
 * clicked occurrence).
 *
 * Unlike `generateAssignmentRRule`, this produces a repeating (no COUNT /
 * UNTIL) rule that mirrors the schedule's full recurrence pattern while
 * restricting BYHOUR/BYMINUTE to only the instance the user interacted with.
 *
 * @param {object} event     - The selected calendar event (event.start is local display time).
 * @param {object} schedule  - Schedule object with a `recurrence` string.
 * @returns {string} RRule string.
 */
export function generateAllOccurrencesRRule(event, schedule) {
  const options = RRule.parseString(schedule.recurrence);
  const tzid = options.tzid;
  const tzidOpt = tzid ? { tzid } : {};

  const scheduleTz = tzid || 'utc';
  const eventInTz = DateTime.fromJSDate(event.start, { zone: 'local' }).setZone(scheduleTz);

  const scheduleHours = options.byhour != null ? [].concat(options.byhour) : null;
  const scheduleMinutes = options.byminute != null ? [].concat(options.byminute) : null;

  const instanceHour = scheduleHours
    ? scheduleHours.includes(eventInTz.hour)
      ? eventInTz.hour
      : scheduleHours[0]
    : eventInTz.hour;
  const instanceMinute = scheduleMinutes
    ? scheduleMinutes.includes(eventInTz.minute)
      ? eventInTz.minute
      : scheduleMinutes[0]
    : eventInTz.minute;

  // Mirror every pattern field from the schedule but replace byhour/byminute
  // with the single instance-specific values. Intentionally omit count/until so
  // the assignment covers all future occurrences at this time slot.
  const rruleOptions = {
    freq: options.freq,
    dtstart: options.dtstart,
    byhour: [instanceHour],
    byminute: [instanceMinute],
    ...tzidOpt,
  };
  if (options.interval && options.interval > 1) rruleOptions.interval = options.interval;
  if (options.byweekday) rruleOptions.byweekday = options.byweekday;
  if (options.bymonth) rruleOptions.bymonth = options.bymonth;
  if (options.bymonthday) rruleOptions.bymonthday = options.bymonthday;
  if (options.bysetpos) rruleOptions.bysetpos = options.bysetpos;
  if (options.bysecond) rruleOptions.bysecond = options.bysecond;

  return new RRule(rruleOptions).toString();
}

export function generateAssignmentRRule(event, schedule, mode) {
  const options = RRule.parseString(schedule.recurrence);
  const tzid = options.tzid;
  const tzidOpt = tzid ? { tzid } : {};

  // Convert the selected event's start time into the schedule's own timezone so
  // we can match it against the schedule's byhour/byminute arrays.
  const scheduleTz = tzid || 'utc';
  const eventStart = event.start;
  const eventInTz = DateTime.fromJSDate(eventStart, { zone: 'local' }).setZone(scheduleTz);

  // All hours/minutes defined in the schedule's RRule.
  // [].concat() safely handles both number (1) and array ([1]) returns from RRule.parseString.
  const scheduleHours = options.byhour != null ? [].concat(options.byhour) : null;
  const scheduleMinutes = options.byminute != null ? [].concat(options.byminute) : null;

  // Use only the single hour/minute that matches the selected instance.
  // This ensures a 1 PM selection is not also recorded as a 6 PM occurrence.
  const instanceHour = scheduleHours
    ? scheduleHours.includes(eventInTz.hour)
      ? eventInTz.hour
      : scheduleHours[0]
    : eventInTz.hour;
  const instanceMinute = scheduleMinutes
    ? scheduleMinutes.includes(eventInTz.minute)
      ? eventInTz.minute
      : scheduleMinutes[0]
    : eventInTz.minute;

  const byhour = [instanceHour];
  const byminute = [instanceMinute];

  if (mode === 'calendar') {
    // Build DTSTART as the event's calendar date at byhour:byminute in the schedule's
    // own timezone. RRule stores TZID-aware dtstarts with UTC digits equal to the
    // wall-clock digits in the target timezone, so we construct a UTC Date whose
    // UTC fields directly encode the desired tzid-local date/time.
    const dtstart = DateTime.utc(
      eventInTz.year,
      eventInTz.month,
      eventInTz.day,
      instanceHour,
      instanceMinute,
      0,
    ).toJSDate();
    return new RRule({
      freq: RRule.DAILY,
      count: 1,
      dtstart,
      byhour,
      byminute,
      ...tzidOpt,
    }).toString();
  }

  // For pattern modes the DTSTART comes from the schedule itself (already UTC
  // wall-clock as stored by the RRule parser).
  const scheduleDtstart = options.dtstart;

  if (mode === 'weekly') {
    // Map JS getDay() (0 = Sunday) to the corresponding RRule weekday constant.
    const jsDayToRRuleDay = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA];
    const byweekday = jsDayToRRuleDay[eventStart.getDay()];
    return new RRule({
      freq: RRule.WEEKLY,
      dtstart: scheduleDtstart,
      byweekday,
      byhour,
      byminute,
      ...tzidOpt,
    }).toString();
  }

  if (mode === 'monthly') {
    const bymonthday = eventStart.getDate(); // 1–31
    return new RRule({
      freq: RRule.MONTHLY,
      dtstart: scheduleDtstart,
      bymonthday,
      byhour,
      byminute,
      ...tzidOpt,
    }).toString();
  }

  if (mode === 'yearly') {
    const bymonth = eventStart.getMonth() + 1; // 1–12
    const bymonthday = eventStart.getDate(); // 1–31
    return new RRule({
      freq: RRule.YEARLY,
      dtstart: scheduleDtstart,
      bymonth,
      bymonthday,
      byhour,
      byminute,
      ...tzidOpt,
    }).toString();
  }

  // Unknown mode — fall back to calendar (single-instance) behaviour.
  console.warn('[generateAssignmentRRule] Unknown mode:', mode, '— falling back to calendar mode.');
  const fallbackDtstart = DateTime.utc(
    eventInTz.year,
    eventInTz.month,
    eventInTz.day,
    instanceHour,
    instanceMinute,
    0,
  ).toJSDate();
  return new RRule({
    freq: RRule.DAILY,
    count: 1,
    dtstart: fallbackDtstart,
    byhour,
    byminute,
    ...tzidOpt,
  }).toString();
}

/**
 * Like generateAssignmentRRule in 'weekly' mode, but lets you specify the
 * target weekday (JS day number 0=Sun … 6=Sat) independent of the event's
 * own start day.  Useful for building per-weekday assignment options in the UI.
 *
 * @param {object} event    - The selected calendar event (used for byhour/byminute fallback)
 * @param {object} schedule - The schedule object with a recurrence string
 * @param {number} jsDay    - JS weekday number (0 = Sunday, 6 = Saturday)
 * @returns {string} RRule string
 */
export function generateWeeklyAssignmentRRuleForDay(event, schedule, jsDay) {
  const options = RRule.parseString(schedule.recurrence);
  const tzid = options.tzid;
  const tzidOpt = tzid ? { tzid } : {};

  // Match the selected instance's hour/minute against the schedule's byhour/byminute
  // so we record only the specific time the user selected, not every hour in the schedule.
  const scheduleTz = tzid || 'utc';
  const eventInTz = DateTime.fromJSDate(event.start, { zone: 'local' }).setZone(scheduleTz);
  const scheduleHours = options.byhour != null ? [].concat(options.byhour) : null;
  const scheduleMinutes = options.byminute != null ? [].concat(options.byminute) : null;
  const instanceHour = scheduleHours
    ? scheduleHours.includes(eventInTz.hour)
      ? eventInTz.hour
      : scheduleHours[0]
    : eventInTz.hour;
  const instanceMinute = scheduleMinutes
    ? scheduleMinutes.includes(eventInTz.minute)
      ? eventInTz.minute
      : scheduleMinutes[0]
    : eventInTz.minute;

  const byhour = [instanceHour];
  const byminute = [instanceMinute];
  const jsDayToRRuleDay = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA];
  const byweekday = jsDayToRRuleDay[jsDay];
  return new RRule({
    freq: RRule.WEEKLY,
    dtstart: options.dtstart,
    byweekday,
    byhour,
    byminute,
    ...tzidOpt,
  }).toString();
}

/**
 * Parse an RRULE string into an `RRule` object after applying small fixes.
 *
 * This function normalizes legacy encodings (for example, a `BYNWEEKDAY`
 * clause) by calling `fixBYNWEEKDAY` and then delegates to
 * `RRule.fromString` to produce a usable `RRule` instance.
 *
 * @param {string} rruleString - The RRULE string to fix and parse (e.g. "FREQ=MONTHLY;BYNWEEKDAY=0,1").
 * @throws {Error} When `rruleString` is falsy.
 * @returns {RRule} Parsed RRule instance.
 */
export function fixedRruleFromString(rruleString) {
  if (!rruleString) {
    throw new Error('[rr-fix] No RRULE string provided');
  }

  // Check for BYNWEEKDAY and convert to BYDAY with ordinals if needed
  let newRruleString = fixBYNWEEKDAY(rruleString);
  // Normalize BYWEEKDAY alias and unsigned ordinal weekday tokens (e.g. 1MO → +1MO)
  newRruleString = fixBYWEEKDAY(newRruleString);

  let fixedRrule = RRule.fromString(newRruleString);
  return fixedRrule;
}

/**
 * Generate calendar event objects for all occurrences of a schedule between two dates.
 *
 * This function:
 *   - Parses the schedule's RRULE string (with legacy fixes)
 *   - Converts the input range from local time to the RRULE's timezone (if useTzidFixing is true)
 *   - Generates all matching occurrences in that range
 *   - Converts each occurrence back to local time for display
 *   - Returns an array of event objects with title, start, end, recurrence, colors, and index
 *
 * @param {Object} options - Options object
 * @param {Object} options.schedule - The schedule object (must have recurrence and duration properties)
 * @param {Date} options.startDate - The start date of the range
 * @param {Date} options.endDate - The end date of the range
 * @param {boolean} [options.useTzidFixing=false] - If true, converts input dates from local time to the RRULE's tzid for correct event generation
 * @returns {Array<Object>} Array of event objects for each occurrence, with times in the user's local time zone
 */
export function generateEventsBetween(schedule, startDate, endDate) {
  //Create an RRule object from the schedule's recurrence string, applying fixes for legacy encodings
  let rrule = fixedRruleFromString(schedule.recurrence);

  // Generate occurrences between the converted start and end dates, then convert each occurrence back to local time for display
  let events = rrule.between(startDate, endDate, true).flatMap((date, idx) => {
    // Convert the provided date from "utc" to the local timezone
    let eventStart = getLocalOfRRuleTime(date);
    let eventEnd = new Date(eventStart.getTime() + schedule.duration - 1000);

    return [
      {
        title: getScheduleLabel(schedule),
        start: eventStart,
        end: eventEnd,
        recurrence: schedule.recurrence,
        colors: getScheduleColors(schedule),
        idx: idx,
        definitionId: schedule.id ?? null,
      },
    ];
  });
  if (events.length === 0) {
    console.warn('[rr-gen] No events generated for this schedule in the given date range.');
  }
  return events;
}

///////////////////////////Schedule helpers

// enrich array of schedules
export function enrichSchedules(schedules) {
  return schedules.map((s) => {
    return enrichSchedule(s);
  });
}

// inject label and color props into schedule for easier rendering
export function enrichSchedule(schedule) {
  return {
    ...schedule,
    label: getScheduleLabel(schedule),
    colors: colorMap[schedule.color] || colorMap.blue,
  };
}

// Predefined color map for schedules
const colorMap = {
  red: { bgColor: '#9a4343', textColor: '#fff', barColor: '#6e2e2e' },
  orange: { bgColor: '#b8934e', textColor: '#fff', barColor: '#7a5e2e' },
  yellow: { bgColor: '#b2a25cff', textColor: '#fff', barColor: '#6e5e2e' },
  green: { bgColor: '#4a9a43', textColor: '#fff', barColor: '#2e6e2e' },
  teal: { bgColor: '#439a96', textColor: '#fff', barColor: '#2e6e6c' },
  blue: { bgColor: '#436c9a', textColor: '#fff', barColor: '#1e374e' },
  purple: { bgColor: '#7a43a1', textColor: '#fff', barColor: '#4e2e6e' },
  pink: { bgColor: '#a1437a', textColor: '#fff', barColor: '#6e2e4e' },
  gray: { bgColor: '#6c6c6c', textColor: '#fff', barColor: '#3e3e3e' },
};

export function getScheduleColorMap() {
  return colorMap;
}

// helper to get color props for a schedule event
export function getScheduleColors(schedule) {
  return colorMap[schedule.color] || colorMap.blue;
}

// inject label and color props into schedule for easier rendering
export function getScheduleLabel(schedule) {
  if (!schedule) return 'Event';
  return schedule.name || (schedule.id != null ? `Event #${schedule.id}` : 'Event');
}

// event props for calendar rendering
export function getScheduleEventProps(event) {
  let style = {};
  if (event.colors) {
    style.backgroundColor = event.colors.bgColor;
    style.color = event.colors.textColor;
    style['--bar-color'] = event.colors.barColor;
    style.barColor = event.colors.barColor;
  }
  style.opacity = event.opacity || 1.0;
  return {
    className: 'event-with-bar',
    style,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Assignment RRule merging
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Collapses a `userId → Assignment[]` map so that compatible scoped rrules are
 * merged into a single record before upload:
 *
 *   • Same time-slot on different weekdays → combine byweekday
 *     e.g. WEEKLY on TH at 14:00 + WEEKLY on FR at 14:00
 *          → WEEKLY on TH,FR at 14:00
 *
 *   • Same weekday(s) at different hours   → combine byhour
 *     e.g. WEEKLY on TH at 14:00 + WEEKLY on TH at 16:00
 *          → WEEKLY on TH at 14:00,16:00
 *
 * Single-occurrence rules (count:1), null-recurrence (all-assignments), and
 * deletion markers (enabled:false) pass through untouched.
 * Superseded originals that already have a backend id are marked enabled:false
 * so uploadAssignments will DELETE them.
 *
 * @param {{ [userId: string]: object[] }} assignmentsByUser
 * @returns {{ [userId: string]: object[] }}
 */
export function mergeAssignmentRRules(assignmentsByUser) {
  const out = {};
  for (const [userId, list] of Object.entries(assignmentsByUser)) {
    out[userId] = _mergeUserList(list);
  }
  return out;
}

/** Extract a stable numeric value from an rrule Weekday object or a plain number. */
function _wdNum(wd) {
  return wd !== null && typeof wd === 'object' && 'weekday' in wd ? wd.weekday : wd;
}

/** Sorted, comma-joined string key for an rrule array field (handles Weekday objects). */
function _normKey(arr) {
  if (arr == null) return '';
  return []
    .concat(arr)
    .map(_wdNum)
    .sort((a, b) => a - b)
    .join(',');
}

/**
 * Build a stable group key from rrule options, excluding the one field we are
 * currently merging on.
 */
function _groupKey(opts, exclude) {
  return [
    opts.freq ?? '',
    opts.dtstart ? opts.dtstart.getTime() : 'null',
    opts.tzid || '',
    exclude === 'byweekday' ? '' : _normKey(opts.byweekday),
    exclude === 'byhour' ? '' : _normKey(opts.byhour),
    _normKey(opts.byminute),
    _normKey(opts.bymonth),
    _normKey(opts.bymonthday),
  ].join('|');
}

/**
 * One-pass consolidation: group entries by every rrule field except
 * `fieldToMerge`, then union the `fieldToMerge` values across each group.
 *
 * Each entry shape: `{ opts, representative, superseded[] }`
 */
function _consolidate(entries, fieldToMerge) {
  const groups = new Map();

  for (const e of entries) {
    const key = _groupKey(e.opts, fieldToMerge);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(e);
  }

  const result = [];
  for (const group of groups.values()) {
    if (group.length === 1) {
      result.push(group[0]);
      continue;
    }

    // Union fieldToMerge values, deduplicated by numeric value.
    const seen = new Set();
    const mergedValues = group
      .flatMap((e) => {
        const v = e.opts[fieldToMerge];
        return v == null ? [] : [].concat(v);
      })
      .filter((v) => {
        const n = _wdNum(v);
        if (seen.has(n)) return false;
        seen.add(n);
        return true;
      })
      .sort((a, b) => _wdNum(a) - _wdNum(b));

    const head = group[0];
    result.push({
      opts: { ...head.opts, [fieldToMerge]: mergedValues },
      representative: head.representative,
      superseded: [
        ...(head.superseded || []),
        ...group.slice(1).flatMap((e) => [e.representative, ...(e.superseded || [])]),
      ],
    });
  }
  return result;
}

function _mergeUserList(list) {
  const passThrough = []; // deletions, null-recurrence, unparseable, single-occurrence
  const repeating = []; // { opts, representative, superseded[] }

  for (const a of list) {
    if (a.enabled === false || a.recurrence === null) {
      passThrough.push(a);
      continue;
    }
    let opts = null;
    try {
      opts = RRule.parseString(a.recurrence);
    } catch {
      /* ignore */
    }
    // Single-occurrence rules (count:1) are calendar pinpoints — don't merge.
    if (!opts || opts.count === 1) {
      passThrough.push(a);
      continue;
    }
    repeating.push({ opts, representative: a, superseded: [] });
  }

  if (repeating.length === 0) return list;

  // Group by definition_id before merging so we never conflate different schedules.
  const byDef = {};
  for (const e of repeating) {
    const d = e.representative.definition_id;
    (byDef[d] = byDef[d] || []).push(e);
  }

  const finalMerged = [];
  const allSuperseded = [];

  for (const defEntries of Object.values(byDef)) {
    // Pass 1: same time-slot on different weekdays → merge byweekday
    let current = _consolidate(defEntries, 'byweekday');
    // Pass 2: same weekday(s) at different hours → merge byhour
    current = _consolidate(current, 'byhour');

    for (const e of current) {
      let rruleStr;
      try {
        rruleStr = new RRule(e.opts).toString();
      } catch {
        rruleStr = e.representative.recurrence; // fallback: keep original
      }
      finalMerged.push({ ...e.representative, recurrence: rruleStr });
      allSuperseded.push(...(e.superseded || []));
    }
  }

  // Mark absorbed originals for deletion on the backend if they have an id.
  const deletionMarks = allSuperseded.filter((a) => a.id).map((a) => ({ ...a, enabled: false }));

  return [...passThrough, ...finalMerged, ...deletionMarks];
}
